import { z } from 'zod';
import { uuid } from '../posting.js';
import { provenanceSchema } from '../master-data.js';
import { taxScenarioSchema } from '../documents.js';
import { ustStatusSchema } from '../einstellungen.js';
import { szenarioErgebnisSchema, ustIdPruefstandSchema } from './szenario.js';
/**
 * Die Schreibvertraege der Steuerlogik — Chip `ST-dienst`, Plan Task 8.
 *
 * Spec `docs/superpowers/specs/2026-09-06-steuerlogik-design.md`, Abschnitt
 * 4.3 (`D-11`) und Abschnitt 8 (`D-13`), Plan
 * `docs/superpowers/plans/2026-09-06-steuerlogik-umsetzung.md`, Task 8.
 * Faelle `E-964`, `E-965`.
 *
 * ---
 *
 * **WARUM EINE EIGENE DATEI NEBEN `szenario.ts`.** Jene Datei sagt in ihrem
 * Kopf ausdruecklich: „Es gibt keinen Schreibvertrag. Diese Datei beschreibt
 * eine Rechnung und keinen Weg, an dem etwas gespeichert wuerde." Das bleibt
 * wahr — die Ableitung ist eine reine Funktion, und ihre Eingabe- und
 * Ergebnisform gehoert nicht mit einer Anfrageform in dieselbe Datei. Hier
 * steht, was ueber die Leitung geht: die Auskunft zu einem Beleg, die
 * Ueberschreibung, und der Schalter.
 *
 * **`szenarioErgebnisSchema` wird WIEDERVERWENDET und nicht abgeschrieben.**
 * Die Auskunft traegt das Ergebnis der Ableitung unveraendert; eine zweite,
 * flach gezogene Form waere der Ort, an dem `abgeleitet: false` eines Tages
 * ein `szenario: null` bekaeme — und damit genau das `szenario ?? 'domestic'`
 * ermoeglichte, das `D-14` verbietet.
 */
/**
 * Ein Datum in ISO-Form, so wie es aus `date`-Spalten kommt.
 *
 * Dieselbe Zeile wie in `quelle.ts` und `szenario.ts`, und aus demselben
 * Grund eine eigene: damit ein vertauschtes Feld hier auffaellt und nicht
 * erst beim Vergleichen zweier Daten.
 */
const isoDatumSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet wird ein Datum JJJJ-MM-TT.');
/**
 * Was der Dienst ueber das Steuerszenario eines Belegs sagt.
 *
 * **Drei Angaben nebeneinander, und keine davon ersetzt eine andere:** was am
 * Beleg GESPEICHERT ist, was sich zum Leistungsstichtag ABLEITEN laesst, und
 * ob beides auseinanderfaellt. Eine Auskunft, die nur das abgeleitete Szenario
 * naennte, liesse die Frage offen, die ein Sachbearbeiter vor dem Ausstellen
 * wirklich hat.
 *
 * **`ableitung` ist `null`, wenn es keinen Leistungsstichtag gibt.** Das ist
 * der planmaessige Zustand eines frischen Entwurfs ohne Leistungsdatum, ohne
 * Leistungsmonat und ohne vollstaendige Positionszeitraeume — die Pflicht
 * greift bei der AUSSTELLUNG und nicht beim Erfassen (`E-972`). Ein
 * Rueckfall auf „heute" waere die erfundene Antwort, die
 * `leistungsstichtag_beleg()` mit seinem `NULL` gerade verhindert.
 *
 * **`abweichung` ist keine Ableitung aus den anderen Feldern, die der Leser
 * selbst machen soll.** Sie ist `true`, wenn die Ableitung ein ANDERES
 * Szenario liefert als das gespeicherte, und ebenso, wenn sie ueberhaupt
 * keines liefert (`abgeleitet: false` — dann steht am Beleg ein Szenario, das
 * die Ausstellung abweisen wird). Ohne Stichtag ist sie `false`: Es gibt
 * nichts zu vergleichen, und ein `true` ohne Vergleich waere eine Warnung
 * ohne Inhalt.
 */
export const steuerszenarioAuskunftSchema = z
    .object({
    documentId: uuid,
    /** Was in `document.tax_scenario` steht. */
    gespeichertesSzenario: taxScenarioSchema,
    /** Was in `document.tax_scenario_source` steht — `declared` oder `derived`. */
    gespeicherteQuelle: provenanceSchema,
    /** Der **Leistungsstichtag** aus `leistungsstichtag_beleg()`, nie das Buchungsdatum. */
    stichtag: isoDatumSchema.nullable(),
    /** Der Stand des OSS-Schalters, unter dem die Ableitung gerechnet wurde (`D-13`). */
    ossSchalter: z.boolean(),
    /** Das Ergebnis der Ableitung, oder `null` ohne ableitbaren Stichtag. */
    ableitung: szenarioErgebnisSchema.nullable(),
    /**
     * Der Pruefstand der USt-IdNr. des Empfaengers **zum Leistungsstichtag**
     * (`ST-web`, Plan Task 9, Nachtrag vom 2026-09-06).
     *
     * **Warum die Auskunft ihn traegt, obwohl die Ableitung ihn schon
     * verbraucht hat.** `ableitung.abgeleitet === false` mit
     * `TAX_SCENARIO_EU_B2B_VAT_ID_REQUIRED` sagt DASS eine gepruefte Nummer
     * fehlt, nicht WELCHE der drei Lagen vorliegt — und die drei verlangen
     * verschiedene Handlungen: eine Nummer erfassen (`fehlt`), eine
     * abgelehnte klaeren (`ungueltig`), eine abgelaufene erneuern
     * (`abgelaufen`). Bis zu diesem Nachtrag stand die Unterscheidung
     * ausschliesslich im Meldungstext der 409 aus `pruefeUstIdPflicht()`,
     * also erst NACH einem Schreibversuch; eine Maske, die sie vorher zeigen
     * will, haette sie sonst im Browser nachrechnen muessen — mit einer
     * zweiten Fassung der Zwoelfmonatsfrist (`D-16b`), und genau die ist die
     * eine Konstante an der einen Stelle.
     *
     * **`null` heisst „kein Leistungsstichtag", nicht „keine Pruefung".**
     * Ohne Stichtag gibt es keinen Tag, gegen den eine Frist laufen koennte;
     * `fehlt` waere an dieser Stelle eine Aussage ueber den Kunden, die
     * niemand getroffen hat. Der Wert ist genau dann `null`, wenn auch
     * `stichtag` und `ableitung` es sind.
     *
     * **Er ist eine AUSKUNFT und keine zweite Bedingung.** Die Pflicht
     * urteilt weiterhin `pruefeUstIdPflicht()` bei der Ausstellung; dieses
     * Feld beantwortet dieselbe Frage vorher, damit die Maske den Weg zur
     * Aufzeichnung anbieten kann statt eine 409 zu provozieren — dieselbe
     * Bauart wie `einschaltbar` am OSS-Schalter unten.
     */
    ustIdPruefstand: ustIdPruefstandSchema.nullable(),
    abweichung: z.boolean(),
})
    .strict();
/**
 * Die Anfrage, mit der ein abgeleitetes Szenario ueberschrieben wird
 * (`D-11`, `E-964`).
 *
 * **Die Begruendung ist Pflicht und steht im VERTRAG und nicht im Dienst.**
 * Ein leerer Rumpf, eine leere Zeichenkette und eine Zeichenkette aus
 * Leerzeichen sind derselbe Fall und bekommen dieselbe 400 mit
 * `VALIDATION_FAILED` — dieselbe Bauart wie `begruendung` an
 * `zahlungZuordnungAufhebungRequestSchema`. Ein Dienst, der die Pflicht selbst
 * pruefte, waere der zweite Ort, an dem sie steht, und der erste, an dem sie
 * eines Tages fehlt.
 *
 * **`trim()` vor `min(1)`**, damit ein Leerzeichen keine Begruendung ist. Die
 * Obergrenze ist grosszuegig und trotzdem vorhanden: Der Text landet im
 * Nutzdatenteil von `audit_log` und im Befundbericht, und ein unbegrenztes
 * Feld ueber eine oeffentliche Leitung ist kein Feld, sondern eine Ablage.
 */
export const szenarioUeberschreibenRequestSchema = z
    .object({
    szenario: taxScenarioSchema,
    begruendung: z.string().trim().min(1).max(2000),
})
    .strict();
/**
 * Der OSS-Schalter, wie ihn der Dienst ausgibt (`D-13`, `E-965`).
 *
 * **`einschaltbar` ist eine ANTWORT und keine zweite Wahrheit ueber die
 * Sperre.** Die Sperre selbst sitzt im Trigger `assert_steuer_einstellung()`
 * (Migration `0179`) und weist ein `UPDATE` ab, solange `steuersatz` keine
 * Zeile ausserhalb von `DE` fuehrt. Dieses Feld beantwortet dieselbe Frage
 * VOR dem Versuch, damit eine Oberflaeche den Schalter ausgegraut anzeigen
 * kann statt eine 409 zu provozieren — es ersetzt die Sperre nicht und wird
 * aus derselben Bedingung gelesen.
 */
export const ossSchalterSchema = z
    .object({
    aktiv: z.boolean(),
    /** Ob die Steuersatzquelle Saetze ausserhalb von `DE` fuehrt (`D-12`). */
    einschaltbar: z.boolean(),
    geaendertAm: z.string().min(1),
    geaendertVon: uuid,
})
    .strict();
/**
 * Die Anfrage, mit der der OSS-Schalter umgelegt wird.
 *
 * **Ein Feld und keine Begruendung.** Anders als beim Ueberschreiben eines
 * Szenarios (`D-11`) verlangt `D-13` hier keine — der Vorgang ist keine
 * Abweichung von einer Ableitung, sondern eine Betriebsentscheidung, und wer
 * sie treffen darf, sagt die Faehigkeit. Wer, wann und wohin geschaltet hat,
 * steht in `steuer_einstellung.geaendert_von`/`geaendert_am` und zusaetzlich
 * im Protokoll.
 */
export const ossSchalterSetzenRequestSchema = z
    .object({
    aktiv: z.boolean(),
})
    .strict();
/**
 * Ein Eintrag des Satzangebots — ein Steuersatz mit Schluessel und
 * vorgeschlagenem Erloeskonto, so wie die Positionsmaske ihn anbietet
 * (Auftrag `PF-kuszenario`, Spec Systemeinstellungen 3.4, `D-35`, `E-1631`).
 *
 * **Satz und Schluessel gehoeren zusammen** und werden gemeinsam gewaehlt;
 * das Konto ist ein VORSCHLAG fuer eine noch leere Position, keine Pflicht.
 * `rate` ist ein Faktor als Zeichenkette (`'0.19'`, `'0'`) und nie eine
 * Zahl (Hausregel 1).
 */
export const satzangebotEintragSchema = z
    .object({
    rate: z.string().regex(/^[0-9]+(\.[0-9]+)?$/, 'Erwartet wird ein Faktor wie 0.19.'),
    key: z.string().min(1),
    konto: z.string().regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern'),
    bezeichnung: z.string().min(1),
})
    .strict();
/**
 * Das Satzangebot zu einem Stichtag — `GET /api/v1/steuer/satzangebot`.
 *
 * **Es folgt der Steuerfassung am Stichtag** (`steuerprofil`, `D-2`): Unter
 * `regel` die drei Eintraege, die die Oberflaeche bis zu diesem Vertrag fest
 * fuehrte (19 % auf 8400, 7 % auf 8300, 0 % auf 8125), unter
 * `kleinunternehmer` genau einer — Satz 0, Hauswert `'0'`, das Erloeskonto
 * der Fassung (`erloeskonto_kleinunternehmer`). Der Stichtag ist der
 * **Leistungsstichtag** der Position, nie das Buchungsdatum.
 */
export const satzangebotSchema = z
    .object({
    stichtag: isoDatumSchema,
    steuerprofilId: uuid,
    ustStatus: ustStatusSchema,
    eintraege: z.array(satzangebotEintragSchema).min(1),
})
    .strict();
/** Die Anfrage: ein optionaler Stichtag, sonst heute (`Europe/Berlin`). */
export const satzangebotAnfrageSchema = z
    .object({
    stichtag: isoDatumSchema.optional(),
})
    .strict();
//# sourceMappingURL=dienst.js.map